import { InputLabel, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { styleInputDefault } from "@/theme";
import { isValid as isValidPolicyNumber } from "@/utils/validators/PolicyNumber/PolicyNumber";
import DateOfBirthInput, {
  DOBFormData,
} from "@/features/Common/components/DateOfBirthInput";
import { UserType } from "../../index";

interface CollectUserPersonalInformationProps {
  onChange: (value: any) => void;
  userType: UserType | null;
}

export default function CollectUserPersonalInformation({
  onChange,
  userType,
}: CollectUserPersonalInformationProps) {
  const { t } = useTranslation();
  const policyNumberInputID = useId();

  const [policyNumber, setPolicyNumber] = useState<string>("");
  const [dateOfBirth, setDateOfBirth] = useState<string | undefined>();
  const [isDateOfBirthValid, setIsDateOfBirthValid] = useState(false);

  const isCustomer = userType === UserType.CUSTOMER;

  useEffect(() => {
    const isPolicyValid = isCustomer
      ? Boolean(isValidPolicyNumber(Number(policyNumber)))
      : true;

    onChange({
      isValid: isPolicyValid && isDateOfBirthValid,
      data: {
        policyNumber: isCustomer ? policyNumber : null,
        dateOfBirth,
      },
    });
  }, [policyNumber, dateOfBirth, isDateOfBirthValid, isCustomer]);

  function onPolicyNumberInput(
    event: React.ChangeEvent<HTMLInputElement>
  ): void {
    const { value } = event.target;

    setPolicyNumber(value);
  }

  function onDateOfBirthChange(dobData: DOBFormData, isValid: boolean) {
    if (isValid) {
      const formattedDate = `${dobData.year}-${dobData.month.padStart(
        2,
        "0"
      )}-${dobData.day.padStart(2, "0")}`;
      setDateOfBirth(formattedDate);
    }
    setIsDateOfBirthValid(isValid);
  }

  return (
    <Stack spacing={4} justifyContent="flex-end">
      <form>
        <Stack spacing={2} justifyContent="flex-end">
          <Typography variant="h3" sx={{ pb: 2 }}>
            {t("PublicBinahScanFlow.collectUserPersonalInformation.title")}
          </Typography>

          {isCustomer && (
            <Stack spacing={1} sx={{ pb: 2 }}>
              <InputLabel htmlFor={policyNumberInputID}>
                <Typography
                  color="neutral.700"
                  variant="body2"
                  fontWeight={300}
                >
                  {t(
                    "PublicBinahScanFlow.collectUserPersonalInformation.policyNumber"
                  )}
                </Typography>
              </InputLabel>
              <TextField
                id={policyNumberInputID}
                value={policyNumber}
                onInput={onPolicyNumberInput}
                placeholder={t("common.enterValue")}
                slotProps={{
                  htmlInput: {
                    autoComplete: "off",
                    "aria-label": t(
                      "PublicBinahScanFlow.collectUserPersonalInformation.policyNumber"
                    ),
                  },
                }}
                error={Boolean(
                  policyNumber && !isValidPolicyNumber(Number(policyNumber))
                )}
                size="small"
                sx={{
                  input: {
                    ...styleInputDefault,
                  },
                }}
                fullWidth
              />
            </Stack>
          )}

          <Stack spacing={1}>
            <InputLabel>
              <Typography color="neutral.700" variant="body2" fontWeight={300}>
                {t("QuestionTypeDateOfBirth.title")}
              </Typography>
            </InputLabel>
            <DateOfBirthInput onChange={onDateOfBirthChange} />
          </Stack>
        </Stack>
      </form>
    </Stack>
  );
}
